import { createContext, useContext, useEffect, ReactNode, useMemo } from 'react';
import { useSiteSettings } from './SiteSettingsContext';
import { useAuthMe } from '@/hooks/useAuthMe';
import { useUserTheme } from '@/hooks/useUserTheme';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * ThemeProvider - IW3.17.C: Per-user theme when logged in; system theme when not.
 * When logged in: theme from user preference ui.theme; on toggle persist to user prefs.
 * When not logged in: theme from system settings.
 */
export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const { data: user } = useAuthMe();
  const { settings, updateTheme } = useSiteSettings();

  // Early boot userId from localStorage (so we can resolve user theme before auth hydrate)
  const bootUserId = useMemo(() => {
    if (typeof window === 'undefined') return null;
    try {
      const userStr = window.localStorage.getItem('user');
      if (!userStr) return null;
      const parsed = JSON.parse(userStr) as { id?: string } | null;
      if (!parsed || !parsed.id) return null;
      return String(parsed.id);
    } catch {
      return null;
    }
  }, []);

  const runtimeUserId = user?.id ?? null;

  // Runtime user theme (tied to auth user)
  const { userTheme, setUserTheme } = useUserTheme(runtimeUserId);
  // Boot-time user theme from localStorage (not tied to auth lifecycle)
  const { userTheme: bootUserTheme } = useUserTheme(bootUserId);

  const hasRuntimeUser = !!runtimeUserId;

  // Effective dark:
  // - Logged-in: per-user theme (runtime or boot) > system theme
  // - Logged-out: last boot user theme if any, otherwise FORCE LIGHT (ignore system dark)
  const effectiveDark = useMemo(() => {
    if (hasRuntimeUser) {
      if (userTheme !== null && userTheme !== undefined) {
        return userTheme.is_dark_mode;
      }
      if (
        bootUserId &&
        bootUserId === runtimeUserId &&
        bootUserTheme !== null &&
        bootUserTheme !== undefined
      ) {
        return bootUserTheme.is_dark_mode;
      }
      return settings.theme.is_dark_mode;
    }

    if (bootUserId && bootUserTheme !== null && bootUserTheme !== undefined) {
      return bootUserTheme.is_dark_mode;
    }

    // Fresh visitor (no user, no theme cache): default LIGHT, do not fallback to system dark
    return false;
  }, [
    hasRuntimeUser,
    userTheme,
    bootUserId,
    runtimeUserId,
    bootUserTheme,
    settings.theme.is_dark_mode,
  ]);

  const theme: Theme = effectiveDark ? 'dark' : 'light';

  // Apply effective theme to DOM (overrides system theme when user pref exists)
  useEffect(() => {
    document.documentElement.classList.toggle('dark', effectiveDark);
  }, [effectiveDark]);

  const toggleTheme = async () => {
    const nextDark = !effectiveDark;
    try {
      if (runtimeUserId) {
        await setUserTheme({ is_dark_mode: nextDark });
      } else {
        await updateTheme({ is_dark_mode: nextDark });
      }
    } catch (error) {
      console.error('Failed to toggle theme:', error);
    }
  };

  const setTheme = async (newTheme: Theme) => {
    const nextDark = newTheme === 'dark';
    try {
      if (runtimeUserId) {
        await setUserTheme({ is_dark_mode: nextDark });
      } else {
        await updateTheme({ is_dark_mode: nextDark });
      }
    } catch (error) {
      console.error('Failed to set theme:', error);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
